import React, { FC, useEffect, useState, useMemo, useCallback } from 'react';
import Box from '@material-ui/core/Box';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Typography from '@material-ui/core/Typography';
import {
  CircularProgress,
  IconButton,
  InputBase,
  Paper,
  Popover,
  SwipeableDrawer,
  Tooltip as MuiTooltip,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';
import {
  ChevronLeft,
  ChevronRight,
  Close,
  FiberManualRecord,
  Send,
  Settings,
  Tune,
  TrendingDown,
  TrendingUp,
} from '@material-ui/icons';

import clsx from 'clsx';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQuery } from '@apollo/client';
import {
  EXECUTIVE_PERFORMANCE_SUMMARY_QUERY,
  EXECUTIVE_KPI_EARLIEST_DATE_QUERY,
  ExecutiveKpiEarliestDateData,
  ExecutivePerformanceSummaryData,
  ExecutivePerformanceSummaryVars,
  toAnalyticsPeriod,
} from '@/graph/group-performance';
import ExecutiveAdminDialog from './components/ExecutiveAdminDialog';
import SendExecutiveReportDialog from './components/SendExecutiveReportDialog';
import DayKpiTile from './components/DayKpiTile';
import { History, Location } from 'history';
import useDebugMode from '@/hooks/useDebugMode';

// Default threshold configuration per metric.
// Each entry stores the "good" and "warning" boundaries along with direction.
// The user can override the "good" threshold; the warning boundary scales proportionally.
const DEFAULT_THRESHOLDS: Record<string, { good: number; warning: number; inverse: boolean }> = {
  fcots: { good: 90, warning: 80, inverse: false },
  lpod: { good: 10, warning: 20, inverse: true },
  turnover: { good: 20, warning: 25, inverse: true },
  orsUtilization: { good: 85, warning: 75, inverse: false },
  numberOfCases: { good: 140, warning: 100, inverse: false },
  rtor: { good: 45, warning: 60, inverse: true },
  orThroughput: { good: 1.8, warning: 1.5, inverse: false },
  ptThroughput: { good: 4.0, warning: 3.5, inverse: false },
  waitTime: { good: 25, warning: 35, inverse: true },
  prepTime: { good: 15, warning: 20, inverse: true },
  cancellation: { good: 1.0, warning: 2.0, inverse: true },
};

const THRESHOLD_STORAGE_KEY = 'executive-analytics-thresholds';

const loadSavedThresholds = (): Record<string, number> => {
  try {
    const raw = localStorage.getItem(THRESHOLD_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const saveThresholds = (thresholds: Record<string, number>) => {
  localStorage.setItem(THRESHOLD_STORAGE_KEY, JSON.stringify(thresholds));
};

const METRIC_ORDER_STORAGE_KEY = 'executive-analytics-metric-order';
// Phase 1 KPIs that are available from API (for reference)
// const PHASE1_METRICS = ['fcots', 'turnover', 'orsUtilization', 'numberOfCases'];
// Full list including future Phase 2 metrics (currently hidden/unavailable)
// Note: the internal key 'fcots' (First Case On-Time Start) maps to the on-time
// percentage. Average delay minutes across late first cases is surfaced as the
// secondary value on the FCOTS card.
const DEFAULT_METRIC_ORDER = [
  'numberOfCases',
  'turnover',
  'orsUtilization',
  'fcots',
  'lpod',
  'rtor',
  // Phase 2 metrics (not yet available from API):
  // 'lastPatientOutDelay',
  // 'waitTime',
  // 'cancellation',
];

const loadSavedMetricOrder = (): string[] => {
  try {
    const raw = localStorage.getItem(METRIC_ORDER_STORAGE_KEY);
    if (!raw) return DEFAULT_METRIC_ORDER;

    // Parse saved order
    const saved: string[] = JSON.parse(raw);

    // Ensure robustness:
    // 1. Keep only keys that still exist in DEFAULT_METRIC_ORDER
    const validSaved = saved.filter(key => DEFAULT_METRIC_ORDER.includes(key));

    // 2. Add any new keys from DEFAULT_METRIC_ORDER that weren't in saved
    const newKeys = DEFAULT_METRIC_ORDER.filter(key => !validSaved.includes(key));

    return [...validSaved, ...newKeys];
  } catch {
    return DEFAULT_METRIC_ORDER;
  }
};

const saveMetricOrder = (order: string[]) => {
  localStorage.setItem(METRIC_ORDER_STORAGE_KEY, JSON.stringify(order));
};

// Generic indication function that honours user-configurable thresholds.
// When the "good" threshold is changed, the "warning" boundary scales proportionally
// to preserve the original gap ratio.
const getIndicationFromThreshold = (
  value: number,
  metricKey: string,
  customGoodThreshold?: number
): 'good' | 'warning' | 'severe' => {
  const config = DEFAULT_THRESHOLDS[metricKey];
  if (!config) return 'good';

  const good = customGoodThreshold ?? config.good;
  const warningRatio = config.warning / config.good;
  const warning = good * warningRatio;

  if (config.inverse) {
    // Lower is better
    if (value <= good) return 'good';
    if (value <= warning) return 'warning';
    return 'severe';
  }
  // Higher is better
  if (value >= good) return 'good';
  if (value >= warning) return 'warning';
  return 'severe';
};

// Color helpers
const getIndicationColor = (indication: 'good' | 'warning' | 'severe' | undefined) => {
  switch (indication) {
    case 'good':
      return '#10b981';
    case 'warning':
      return '#f59e0b';
    case 'severe':
      return '#ef4444';
    default:
      return '#f8fafc';
  }
};

const getIndicationGlow = (indication: 'good' | 'warning' | 'severe' | undefined) => {
  switch (indication) {
    case 'good':
      return 'rgba(16, 185, 129, 0.5)';
    case 'warning':
      return 'rgba(245, 158, 11, 0.5)';
    case 'severe':
      return 'rgba(239, 68, 68, 0.5)';
    default:
      return 'rgba(248, 250, 252, 0.35)';
  }
};

const getMetricVisualColor = (tone: 'red' | 'white' | 'green') => {
  if (tone === 'red') return '#ef4444';
  if (tone === 'green') return '#10b981';
  return '#f8fafc';
};

const formatMetricValue = (value: number, suffix: string) => `${Math.round(value * 10) / 10}${suffix}`;

// Metric definition type
interface MetricDef {
  key: string;
  title: string;
  shortTitle: string;
  value: number;
  previousValue: number;
  suffix: string;
  indication: 'good' | 'warning' | 'severe';
  description: string;
  cardDescription?: string;
  inverseTrend?: boolean;
  threshold: number;
  unavailable?: boolean;
  previousAvailable?: boolean;
  secondaryValue?: string;
  secondarySuffix?: string;
  secondaryDescription?: string;
  tone: 'red' | 'white' | 'green';
  secondaryTone?: 'red' | 'white' | 'green';
  note?: string;
}

// KPI Card component (left sidebar)
interface KpiCardProps {
  index: number;
  metric: MetricDef;
  isSelected: boolean;
  onClick: () => void;
}

const KpiCard: FC<KpiCardProps> = ({ index, metric, isSelected, onClick }) => {
  const classes = useStyles();

  const diff = metric.value - metric.previousValue;
  const percentChange = metric.previousValue ? ((diff / Math.abs(metric.previousValue)) * 100).toFixed(1) : '0.0';
  const isPositive = diff > 0;
  const isInverseTrend = metric.inverseTrend;
  const isGood = isInverseTrend ? !isPositive : isPositive;
  const trendColor = isGood ? '#10b981' : '#ef4444';
  const color = getMetricVisualColor(metric.tone);
  const secondaryColor = getMetricVisualColor(metric.secondaryTone ?? metric.tone);

  return (
    <Paper
      className={clsx(classes.kpiCard, {
        [classes.kpiCardSelected]: isSelected,
      })}
      elevation={0}
      onClick={onClick}
    >
      <Box className={classes.kpiCardHeader}>
        <Typography className={classes.kpiCardIndex}>
          {index + 1}. {metric.shortTitle}
        </Typography>
        {isSelected && <FiberManualRecord style={{ fontSize: 8, color: '#22d3ee', marginLeft: 'auto' }} />}
      </Box>
      <Box className={classes.kpiCardContent}>
        <Box className={classes.kpiCardBody}>
          <Typography
            className={classes.kpiCardValue}
            style={{ color: metric.unavailable ? 'rgba(255,255,255,0.25)' : color, cursor: 'default' }}
            title={metric.cardDescription || metric.description}
          >
            {metric.unavailable
              ? '—'
              : metric.suffix?.includes('MIN')
                ? Math.round(metric.value)
                : Math.round(metric.value * 10) / 10}
            {!metric.unavailable && <span className={classes.kpiCardUnit}>{metric.suffix}</span>}
          </Typography>
          {!metric.unavailable && metric.secondaryValue && (
            <Typography
              className={classes.kpiCardValue}
              style={{ color: secondaryColor, marginLeft: 16, cursor: 'default', fontSize: '1.8rem' }}
              title={metric.secondaryDescription}
            >
              {metric.secondaryValue}
              {metric.secondarySuffix && <span className={classes.kpiCardUnit}>{metric.secondarySuffix}</span>}
            </Typography>
          )}
        </Box>
        <Box className={classes.kpiCardTrend}>
          {metric.unavailable ? null : metric.previousAvailable === false ? (
            <Typography className={classes.kpiCardTrendText} style={{ color: 'rgba(255,255,255,0.3)' }}>
              No previous data
            </Typography>
          ) : (
            <>
              {isPositive ? (
                <TrendingUp style={{ fontSize: 22, color: trendColor, marginRight: 4 }} />
              ) : (
                <TrendingDown style={{ fontSize: 22, color: trendColor, marginRight: 4 }} />
              )}
              <Typography className={classes.kpiCardTrendText} style={{ color: trendColor }}>
                {isPositive ? '+' : ''}
                {percentChange}%
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

// Sortable Wrapper for KPI Card
interface SortableKpiCardProps extends KpiCardProps {
  id: string;
}

const SortableKpiCard: FC<SortableKpiCardProps> = props => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    position: 'relative' as const,
    opacity: isDragging ? 0.9 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <KpiCard {...props} />
    </div>
  );
};

// Custom tooltip for chart
const CustomTooltip = ({ active, payload, label, suffix, secondarySuffix }: any) => {
  if (active && payload && payload.length) {
    const primary = payload.find((p: any) => p.dataKey === 'value');
    const secondary = payload.find((p: any) => p.dataKey === 'secondary');
    const formatPrimary = (v: number) => (suffix?.includes('MIN') ? Math.round(v) : Math.round(v * 10) / 10);
    return (
      <Box
        style={{
          background: 'rgba(15, 23, 42, 0.95)',
          border: '1px solid rgba(34, 211, 238, 0.3)',
          borderRadius: 8,
          padding: '8px 12px',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Typography style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', marginBottom: 2 }}>{label}</Typography>
        {primary && (
          <Typography style={{ color: '#22d3ee', fontSize: '0.9rem', fontWeight: 700 }}>
            {formatPrimary(primary.value)}
            {suffix}
          </Typography>
        )}
        {secondary && secondary.value != null && (
          <Typography style={{ color: '#a78bfa', fontSize: '0.8rem', fontWeight: 600 }}>
            {Math.round(secondary.value)}
            {secondarySuffix}
          </Typography>
        )}
      </Box>
    );
  }
  return null;
};

// Custom dot for chart
const CustomDot = (props: any) => {
  const { cx, cy } = props;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={3}
      fill="#22d3ee"
      stroke="rgba(34, 211, 238, 0.5)"
      strokeWidth={2}
      style={{ filter: 'drop-shadow(0 0 4px rgba(34, 211, 238, 0.8))' }}
    />
  );
};

// Trend Intelligence Panel (right side)
type TrendDataPoint = { day: string; value: number; secondary?: number };

interface TrendPanelProps {
  metric: MetricDef;
  trendData: TrendDataPoint[];
  periodKey: TimePeriod;
  onThresholdChange?: (metricKey: string, value: number) => void;
}

// Label shown beneath the big headline value so users know the value
// represents the average / total for the selected period.
const getPeriodAggregateLabel = (period: TimePeriod, metricKey: string): string => {
  const isTotal = metricKey === 'numberOfCases';
  const aggregator = isTotal ? 'Total' : 'Average';
  switch (period) {
    case 'd':
      return isTotal ? 'Daily Total' : 'Daily Value';
    case 'w':
      return `Weekly ${aggregator}`;
    case 'm':
      return `Monthly ${aggregator}`;
    case 'q':
      return `Quarterly ${aggregator}`;
    case 'y':
      return `Annual ${aggregator}`;
  }
};

const TrendPanel: FC<TrendPanelProps> = ({ metric, trendData, periodKey, onThresholdChange }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [inputValue, setInputValue] = useState('');
  const hasSecondary = trendData.some(d => d.secondary != null);

  const handleOpenSettings = (e: React.MouseEvent<HTMLElement>) => {
    setInputValue(String(metric.threshold));
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleApply = () => {
    const parsed = parseFloat(inputValue);
    if (!isNaN(parsed) && parsed > 0) {
      onThresholdChange?.(metric.key, parsed);
    }
    handleClose();
  };

  const diff = metric.value - metric.previousValue;
  const percentChange = metric.previousValue ? ((diff / Math.abs(metric.previousValue)) * 100).toFixed(1) : '0.0';
  const isPositive = diff > 0;
  const isInverseTrend = metric.inverseTrend;
  const isGood = isInverseTrend ? !isPositive : isPositive;
  const trendColor = isGood ? '#10b981' : '#ef4444';
  const color = getMetricVisualColor(metric.tone);
  const secondaryColor = getMetricVisualColor(metric.secondaryTone ?? metric.tone);

  return (
    <Paper className={classes.trendPanel} elevation={0}>
      {/* Header */}
      <Box className={classes.trendHeader}>
        <Box>
          <Typography className={classes.trendTitle}>{metric.title}</Typography>
          <Typography className={classes.trendDescription}>{metric.description}</Typography>
        </Box>
        <Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
          <Box className={classes.trendLiveValue} mr={1}>
            <Typography
              className={classes.trendLiveNumber}
              style={{ color: metric.unavailable ? 'rgba(255,255,255,0.25)' : color }}
            >
              {metric.unavailable
                ? '—'
                : metric.suffix?.includes('MIN')
                  ? Math.round(metric.value)
                  : Math.round(metric.value * 10) / 10}
              {!metric.unavailable && <span className={classes.trendLiveUnit}>{metric.suffix}</span>}
            </Typography>
            {!metric.unavailable && metric.secondaryValue && (
              <Typography
                className={classes.trendLiveNumber}
                style={{ color: secondaryColor, marginTop: 2, fontSize: '2.2rem' }}
                title={metric.secondaryDescription}
              >
                {metric.secondaryValue}
                {metric.secondarySuffix && <span className={classes.trendLiveUnit}>{metric.secondarySuffix}</span>}
              </Typography>
            )}
            {!metric.unavailable && (
              <Typography className={classes.trendLiveLabel}>{getPeriodAggregateLabel(periodKey, metric.key)}</Typography>
            )}
            {!metric.unavailable && metric.note && (
              <Typography className={classes.trendNote}>{metric.note}</Typography>
            )}
          </Box>
          <IconButton className={classes.thresholdButton} onClick={handleOpenSettings} size="small">
            <Settings style={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>
      {/* Threshold settings popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: classes.thresholdPopover }}
      >
        <Typography className={classes.thresholdPopoverLabel}>TARGET THRESHOLD</Typography>
        <Box className={classes.thresholdInputRow}>
          <InputBase
            className={classes.thresholdInput}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleApply();
            }}
            type="number"
            autoFocus
          />
          {metric.suffix.trim() && (
            <Typography className={classes.thresholdInputSuffix}>{metric.suffix.trim()}</Typography>
          )}
        </Box>
        <Box className={classes.thresholdApplyButton} onClick={handleApply}>
          <Typography className={classes.thresholdApplyText}>Apply</Typography>
        </Box>
      </Popover>

      {/* Chart */}
      <Box className={classes.trendChartContainer}>
        {trendData.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            style={{ opacity: 0.5 }}
          >
            <Typography
              style={{
                color: 'rgba(255,255,255,0.4)',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}
            >
              No trend data available
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trendData} margin={{ top: 10, right: hasSecondary ? 40 : 20, left: 0, bottom: 10 }}>
              <defs>
                <linearGradient id="lineGlow" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
              <XAxis
                dataKey="day"
                stroke="rgba(255,255,255,0.2)"
                tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 13, fontWeight: 600 }}
                tickLine={false}
                axisLine={{ stroke: 'rgba(255,255,255,0.06)' }}
                interval={4}
              />
              <YAxis
                yAxisId="left"
                stroke="rgba(255,255,255,0.2)"
                tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 13, fontWeight: 600 }}
                tickLine={false}
                axisLine={false}
                width={48}
              />
              {hasSecondary && (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="rgba(255,255,255,0.2)"
                  tick={{ fill: 'rgba(167, 139, 250, 0.65)', fontSize: 13, fontWeight: 600 }}
                  tickLine={false}
                  axisLine={false}
                  width={48}
                />
              )}
              <Tooltip
                content={<CustomTooltip suffix={metric.suffix} secondarySuffix={metric.secondarySuffix} />}
                wrapperStyle={{ transition: 'none' }}
              />
              <ReferenceLine
                yAxisId="left"
                y={metric.threshold}
                stroke="#f59e0b"
                strokeDasharray="6 3"
                strokeWidth={1.5}
                ifOverflow="extendDomain"
                label={{
                  value: `Target: ${metric.threshold}${metric.suffix}`,
                  fill: 'rgba(245, 158, 11, 0.85)',
                  fontSize: 13,
                  fontWeight: 700,
                  position: 'insideTopRight',
                }}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="value"
                isAnimationActive={false}
                stroke="#22d3ee"
                strokeWidth={2}
                dot={<CustomDot />}
                activeDot={{ r: 6, fill: '#22d3ee', stroke: '#0f172a', strokeWidth: 2 }}
                style={{ filter: 'drop-shadow(0 0 8px rgba(34, 211, 238, 0.6))' }}
              />
              {hasSecondary && (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="secondary"
                  isAnimationActive={false}
                  stroke="#a78bfa"
                  strokeWidth={1.5}
                  strokeDasharray="4 2"
                  dot={false}
                  activeDot={{ r: 4, fill: '#a78bfa', stroke: '#0f172a', strokeWidth: 2 }}
                  connectNulls
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Box>

      {/* Footer */}
      <Box className={classes.trendFooter}>
        <Box className={classes.trendFooterItem}>
          <Typography className={classes.trendFooterLabel}>VARIANCE</Typography>
          {metric.unavailable ? (
            <Typography className={classes.trendFooterValue} style={{ color: 'rgba(255,255,255,0.25)' }}>
              —
            </Typography>
          ) : metric.previousAvailable === false ? (
            <Typography className={classes.trendFooterValue} style={{ color: '#f59e0b', opacity: 0.7 }}>
              No previous data
            </Typography>
          ) : (
            <Typography className={classes.trendFooterValue} style={{ color: trendColor }}>
              {isPositive ? '+' : ''}
              {percentChange}% vs Prev
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

// Time Period Picker component — Granularity + Stepper pattern
export type TimePeriod = 'd' | 'w' | 'm' | 'q' | 'y';
export const TIME_PERIOD_KEYS: TimePeriod[] = ['d', 'w', 'm', 'q', 'y'];

const PERIOD_LABELS: Record<TimePeriod, string> = {
  d: 'Day',
  w: 'Week',
  m: 'Month',
  q: 'Quarter',
  y: 'Year',
};

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** URL params that define the period. Exported for use by parent routers. */
export interface PeriodParams {
  t: TimePeriod;
  y: number;
  m?: number; // 1-12
  q?: number; // 1-4
  w?: number; // 1-53
  d?: number; // 1-31
}

/** Build a start-date ISO string from period params */
export const periodParamsToStartDate = (p: PeriodParams): string => {
  switch (p.t) {
    case 'd': {
      const mm = String(p.m ?? 1).padStart(2, '0');
      const dd = String(p.d ?? 1).padStart(2, '0');
      return `${p.y}-${mm}-${dd}`;
    }
    case 'w': {
      // ISO week → Monday of that week (use UTC to avoid timezone shifts)
      const jan4 = new Date(Date.UTC(p.y, 0, 4));
      const dayOfWeek = jan4.getUTCDay() || 7;
      const monday = new Date(jan4);
      monday.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1 + ((p.w ?? 1) - 1) * 7);
      const yy = monday.getUTCFullYear();
      const mm = String(monday.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(monday.getUTCDate()).padStart(2, '0');
      return `${yy}-${mm}-${dd}`;
    }
    case 'm': {
      const mm = String(p.m ?? 1).padStart(2, '0');
      return `${p.y}-${mm}-01`;
    }
    case 'q': {
      const qMonth = ((p.q ?? 1) - 1) * 3 + 1;
      const mm = String(qMonth).padStart(2, '0');
      return `${p.y}-${mm}-01`;
    }
    case 'y':
      return `${p.y}-01-01`;
  }
};

/** Get the ISO week number of a Date */
const getISOWeek = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

/** Get the ISO week-year of a Date */
const getISOWeekYear = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  return d.getUTCFullYear();
};

/** Build PeriodParams for the current period */
export const currentPeriodParams = (t: TimePeriod, now: Date): PeriodParams => {
  switch (t) {
    case 'd':
      return { t, y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() };
    case 'w':
      return { t, y: getISOWeekYear(now), w: getISOWeek(now) };
    case 'm':
      return { t, y: now.getFullYear(), m: now.getMonth() + 1 };
    case 'q':
      return { t, y: now.getFullYear(), q: Math.floor(now.getMonth() / 3) + 1 };
    case 'y':
      return { t, y: now.getFullYear() };
  }
};

/** Step a PeriodParams forward or backward by one period */
const stepPeriod = (p: PeriodParams, direction: 1 | -1): PeriodParams => {
  switch (p.t) {
    case 'd': {
      const d = new Date(p.y, (p.m ?? 1) - 1, (p.d ?? 1) + direction);
      return { t: 'd', y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate() };
    }
    case 'w': {
      const newW = (p.w ?? 1) + direction;
      if (newW < 1) return { t: 'w', y: p.y - 1, w: 52 };
      if (newW > 52) return { t: 'w', y: p.y + 1, w: 1 };
      return { t: 'w', y: p.y, w: newW };
    }
    case 'm': {
      const d = new Date(p.y, (p.m ?? 1) - 1 + direction, 1);
      return { t: 'm', y: d.getFullYear(), m: d.getMonth() + 1 };
    }
    case 'q': {
      let newQ = (p.q ?? 1) + direction;
      let newY = p.y;
      if (newQ < 1) {
        newQ = 4;
        newY--;
      }
      if (newQ > 4) {
        newQ = 1;
        newY++;
      }
      return { t: 'q', y: newY, q: newQ };
    }
    case 'y':
      return { t: 'y', y: p.y + direction };
  }
};

/** Parse URL search params into PeriodParams */
export const parsePeriodParams = (search: string, now: Date): PeriodParams => {
  const params = new URLSearchParams(search);
  const t = (params.get('t') as TimePeriod) || 'm';
  if (!TIME_PERIOD_KEYS.includes(t)) return currentPeriodParams('m', now);

  const y = params.get('y') ? parseInt(params.get('y')!, 10) : now.getFullYear();
  const m = params.get('m') ? parseInt(params.get('m')!, 10) : undefined;
  const q = params.get('q') ? parseInt(params.get('q')!, 10) : undefined;
  const w = params.get('w') ? parseInt(params.get('w')!, 10) : undefined;
  const d = params.get('d') ? parseInt(params.get('d')!, 10) : undefined;

  switch (t) {
    case 'd':
      return { t, y, m: m ?? now.getMonth() + 1, d: d ?? now.getDate() };
    case 'w':
      return { t, y, w: w ?? getISOWeek(now) };
    case 'm':
      return { t, y, m: m ?? now.getMonth() + 1 };
    case 'q':
      return { t, y, q: q ?? Math.floor(now.getMonth() / 3) + 1 };
    case 'y':
      return { t, y };
  }
};

/** Serialize PeriodParams to URL search params (preserving kpi and other params) */
export const periodParamsToSearch = (p: PeriodParams, currentSearch: string): string => {
  const params = new URLSearchParams(currentSearch);
  // Clear old period params
  ['t', 'y', 'm', 'q', 'w', 'd'].forEach(k => params.delete(k));
  params.set('t', p.t);
  params.set('y', String(p.y));
  if (p.m !== undefined) params.set('m', String(p.m));
  if (p.q !== undefined) params.set('q', String(p.q));
  if (p.w !== undefined) params.set('w', String(p.w));
  if (p.d !== undefined) params.set('d', String(p.d));
  // Remove legacy period param
  params.delete('period');
  return params.toString();
};

/** Check if period params represent the current period (i.e. "now" or future) */
export const isCurrentOrFuture = (p: PeriodParams, now: Date): boolean => {
  const current = currentPeriodParams(p.t, now);
  const startA = periodParamsToStartDate(p);
  const startB = periodParamsToStartDate(current);
  return startA >= startB;
};

/**
 * Compute a human-readable label for period params.
 */
const getStepperLabel = (p: PeriodParams, now: Date): string => {
  const current = currentPeriodParams(p.t, now);
  const isCurrent = periodParamsToStartDate(p) === periodParamsToStartDate(current);

  switch (p.t) {
    case 'd': {
      const d = new Date(p.y, (p.m ?? 1) - 1, p.d ?? 1);
      if (isCurrent) return 'Today';
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      if (
        d.getFullYear() === yesterday.getFullYear() &&
        d.getMonth() === yesterday.getMonth() &&
        d.getDate() === yesterday.getDate()
      ) {
        return `Yesterday — ${MONTH_NAMES_SHORT[d.getMonth()]} ${d.getDate()}`;
      }
      return `${MONTH_NAMES_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    }
    case 'w': {
      // Compute Monday of the ISO week
      const jan4 = new Date(p.y, 0, 4);
      const dayOfWeek = jan4.getDay() || 7;
      const monday = new Date(jan4);
      monday.setDate(jan4.getDate() - dayOfWeek + 1 + ((p.w ?? 1) - 1) * 7);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      const prefix = isCurrent ? 'This Week — ' : '';
      return `${prefix}${MONTH_NAMES_SHORT[monday.getMonth()]} ${monday.getDate()} – ${MONTH_NAMES_SHORT[sunday.getMonth()]} ${sunday.getDate()}, ${sunday.getFullYear()}`;
    }
    case 'm': {
      const month = (p.m ?? 1) - 1;
      if (isCurrent) return `This Month — ${MONTH_NAMES[month]}`;
      return `${MONTH_NAMES[month]} ${p.y}`;
    }
    case 'q': {
      const qLabel = `Q${p.q ?? 1}`;
      if (isCurrent) return `This Quarter — ${qLabel} ${p.y}`;
      return `${qLabel} ${p.y}`;
    }
    case 'y': {
      if (isCurrent) return `This Year — ${p.y}`;
      return `${p.y}`;
    }
  }
};

// ── Contextual period selector (popover content) ──

// TODO: Extract MiniCalendar to a shared component and replace react-date-range Calendar
// in kiosk schedule Filters.jsx with it for consistent theming across the app.
const MiniCalendar: FC<{
  selectedDate?: Date;
  earliestDate?: string;
  highlightWeek?: boolean;
  onSelect: (date: Date) => void;
}> = ({ selectedDate, earliestDate, highlightWeek, onSelect }) => {
  const classes = useStyles();
  const now = new Date();
  const [viewYear, setViewYear] = useState(selectedDate?.getFullYear() ?? now.getFullYear());
  const [viewMonth, setViewMonth] = useState(selectedDate?.getMonth() ?? now.getMonth());

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayOfWeek = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7; // Mon=0
  const cells: (number | null)[] = [
    ...Array(firstDayOfWeek).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const selectedIso = selectedDate
    ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`
    : '';
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  // For week highlighting: compute ISO week of selected date
  const selectedWeekStart = useMemo(() => {
    if (!highlightWeek || !selectedDate) return '';
    const d = new Date(selectedDate);
    const day = d.getDay() || 7;
    d.setDate(d.getDate() - day + 1);
    return d.toISOString().slice(0, 10);
  }, [highlightWeek, selectedDate]);

  const isInSelectedWeek = (dayNum: number): boolean => {
    if (!highlightWeek || !selectedWeekStart) return false;
    const d = new Date(viewYear, viewMonth, dayNum);
    const day = d.getDay() || 7;
    const mon = new Date(d);
    mon.setDate(d.getDate() - day + 1);
    return mon.toISOString().slice(0, 10) === selectedWeekStart;
  };

  const canGoBack = !earliestDate || `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-01` > earliestDate;
  const canGoForward = viewYear < now.getFullYear() || (viewYear === now.getFullYear() && viewMonth < now.getMonth());

  return (
    <Box className={classes.miniCal}>
      <Box className={classes.miniCalHeader}>
        <IconButton
          size="small"
          onClick={() => {
            if (viewMonth === 0) {
              setViewMonth(11);
              setViewYear(y => y - 1);
            } else setViewMonth(m => m - 1);
          }}
          disabled={!canGoBack}
        >
          <ChevronLeft fontSize="small" />
        </IconButton>
        <Typography className={classes.miniCalTitle}>
          {MONTH_NAMES_SHORT[viewMonth]} {viewYear}
        </Typography>
        <IconButton
          size="small"
          onClick={() => {
            if (viewMonth === 11) {
              setViewMonth(0);
              setViewYear(y => y + 1);
            } else setViewMonth(m => m + 1);
          }}
          disabled={!canGoForward}
        >
          <ChevronRight fontSize="small" />
        </IconButton>
      </Box>
      <Box className={classes.miniCalDayNames}>
        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(d => (
          <Typography key={d} className={classes.miniCalDayName}>
            {d}
          </Typography>
        ))}
      </Box>
      <Box className={classes.miniCalGrid}>
        {cells.map((dayNum, i) => {
          if (dayNum === null) return <Box key={`e${i}`} className={classes.miniCalCell} />;
          const iso = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
          const isFuture = iso > todayIso;
          const isBefore = !!earliestDate && iso < earliestDate;
          const disabled = isFuture || isBefore;
          const isSelected = highlightWeek ? isInSelectedWeek(dayNum) : iso === selectedIso;
          const isToday = iso === todayIso;
          return (
            <button
              key={i}
              className={clsx(classes.miniCalCell, classes.miniCalDay, {
                [classes.miniCalDaySelected]: isSelected,
                [classes.miniCalDayToday]: isToday && !isSelected,
                [classes.miniCalDayDisabled]: disabled,
              })}
              disabled={disabled}
              onClick={() => onSelect(new Date(viewYear, viewMonth, dayNum))}
            >
              {dayNum}
            </button>
          );
        })}
      </Box>
    </Box>
  );
};

/** Grid selector for months */
const MonthSelector: FC<{
  selectedYear: number;
  selectedMonth: number;
  earliestDate?: string;
  onSelect: (year: number, month: number) => void;
}> = ({ selectedYear, selectedMonth, earliestDate, onSelect }) => {
  const classes = useStyles();
  const now = new Date();
  const [viewYear, setViewYear] = useState(selectedYear);

  const earliestYear = earliestDate ? parseInt(earliestDate.slice(0, 4), 10) : viewYear - 10;
  const earliestMonth = earliestDate ? parseInt(earliestDate.slice(5, 7), 10) : 1;

  return (
    <Box className={classes.gridSelector}>
      <Box className={classes.miniCalHeader}>
        <IconButton size="small" onClick={() => setViewYear(y => y - 1)} disabled={viewYear <= earliestYear}>
          <ChevronLeft fontSize="small" />
        </IconButton>
        <Typography className={classes.miniCalTitle}>{viewYear}</Typography>
        <IconButton size="small" onClick={() => setViewYear(y => y + 1)} disabled={viewYear >= now.getFullYear()}>
          <ChevronRight fontSize="small" />
        </IconButton>
      </Box>
      <Box className={classes.monthGrid}>
        {MONTH_NAMES_SHORT.map((name, i) => {
          const m = i + 1;
          const isFuture = viewYear > now.getFullYear() || (viewYear === now.getFullYear() && m > now.getMonth() + 1);
          const isBefore = viewYear < earliestYear || (viewYear === earliestYear && m < earliestMonth);
          const isSelected = viewYear === selectedYear && m === selectedMonth;
          return (
            <button
              key={name}
              className={clsx(classes.gridCell, {
                [classes.gridCellSelected]: isSelected,
                [classes.gridCellDisabled]: isFuture || isBefore,
              })}
              disabled={isFuture || isBefore}
              onClick={() => onSelect(viewYear, m)}
            >
              {name}
            </button>
          );
        })}
      </Box>
    </Box>
  );
};

/** Grid selector for quarters */
const QuarterSelector: FC<{
  selectedYear: number;
  selectedQuarter: number;
  earliestDate?: string;
  onSelect: (year: number, quarter: number) => void;
}> = ({ selectedYear, selectedQuarter, earliestDate, onSelect }) => {
  const classes = useStyles();
  const now = new Date();
  const [viewYear, setViewYear] = useState(selectedYear);
  const currentQ = Math.floor(now.getMonth() / 3) + 1;

  const earliestYear = earliestDate ? parseInt(earliestDate.slice(0, 4), 10) : viewYear - 10;
  const earliestQ = earliestDate ? Math.floor((parseInt(earliestDate.slice(5, 7), 10) - 1) / 3) + 1 : 1;

  return (
    <Box className={classes.gridSelector}>
      <Box className={classes.miniCalHeader}>
        <IconButton size="small" onClick={() => setViewYear(y => y - 1)} disabled={viewYear <= earliestYear}>
          <ChevronLeft fontSize="small" />
        </IconButton>
        <Typography className={classes.miniCalTitle}>{viewYear}</Typography>
        <IconButton size="small" onClick={() => setViewYear(y => y + 1)} disabled={viewYear >= now.getFullYear()}>
          <ChevronRight fontSize="small" />
        </IconButton>
      </Box>
      <Box className={classes.quarterGrid}>
        {[1, 2, 3, 4].map(q => {
          const qStartMonth = (q - 1) * 3 + 1;
          const qEndMonth = q * 3;
          const isFuture = viewYear > now.getFullYear() || (viewYear === now.getFullYear() && q > currentQ);
          const isBefore = viewYear < earliestYear || (viewYear === earliestYear && q < earliestQ);
          const isSelected = viewYear === selectedYear && q === selectedQuarter;
          return (
            <button
              key={q}
              className={clsx(classes.gridCell, classes.quarterCell, {
                [classes.gridCellSelected]: isSelected,
                [classes.gridCellDisabled]: isFuture || isBefore,
              })}
              disabled={isFuture || isBefore}
              onClick={() => onSelect(viewYear, q)}
            >
              <span className={classes.quarterLabel}>Q{q}</span>
              <span className={classes.quarterRange}>
                {MONTH_NAMES_SHORT[qStartMonth - 1]} – {MONTH_NAMES_SHORT[qEndMonth - 1]}
              </span>
            </button>
          );
        })}
      </Box>
    </Box>
  );
};

/** List selector for years */
const YearSelector: FC<{
  selectedYear: number;
  earliestDate?: string;
  onSelect: (year: number) => void;
}> = ({ selectedYear, earliestDate, onSelect }) => {
  const classes = useStyles();
  const now = new Date();
  const earliestYear = earliestDate ? parseInt(earliestDate.slice(0, 4), 10) : now.getFullYear() - 10;
  const years: number[] = [];
  for (let y = now.getFullYear(); y >= earliestYear; y--) years.push(y);

  return (
    <Box className={classes.gridSelector}>
      <Box className={classes.yearGrid}>
        {years.map(y => (
          <button
            key={y}
            className={clsx(classes.gridCell, {
              [classes.gridCellSelected]: y === selectedYear,
            })}
            onClick={() => onSelect(y)}
          >
            {y}
          </button>
        ))}
      </Box>
    </Box>
  );
};

export const TimePeriodPicker: FC<{
  value?: TimePeriod;
  periodParams?: PeriodParams;
  earliestDate?: string;
  hideRollupPeriodsWhenEmpty?: boolean;
  disabledPeriods?: TimePeriod[];
  disabledPeriodTooltip?: string;
  onChange?: (params: PeriodParams) => void;
}> = ({
  value = 'm',
  periodParams,
  earliestDate,
  hideRollupPeriodsWhenEmpty = false,
  disabledPeriods = [],
  disabledPeriodTooltip = '',
  onChange,
}) => {
  const classes = useStyles();
  const now = useMemo(() => new Date(), []);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const params = periodParams ?? currentPeriodParams(value, now);
  const availablePeriods = useMemo(
    () =>
      hideRollupPeriodsWhenEmpty && !earliestDate
        ? TIME_PERIOD_KEYS.filter(period => period === 'd')
        : TIME_PERIOD_KEYS,
    [earliestDate, hideRollupPeriodsWhenEmpty]
  );
  const isAtPresent = isCurrentOrFuture(params, now);
  const label = getStepperLabel(params, now);

  // Check if stepping backward would go before earliest available data
  const isAtEarliest = useMemo(() => {
    if (!earliestDate) return false;
    const prev = stepPeriod(params, -1);
    const prevStart = periodParamsToStartDate(prev);
    return prevStart < earliestDate;
  }, [earliestDate, params]);

  const handleGranularityChange = (t: TimePeriod) => {
    if (disabledPeriods.includes(t)) return;
    onChange?.(currentPeriodParams(t, now));
  };

  const handleStepBackward = () => {
    if (!isAtEarliest) {
      onChange?.(stepPeriod(params, -1));
    }
  };

  const handleStepForward = () => {
    if (!isAtPresent) {
      onChange?.(stepPeriod(params, 1));
    }
  };

  const handleLabelClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  // Build the selected date for calendar views
  const selectedDate = useMemo(() => {
    const iso = periodParamsToStartDate(params);
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d);
  }, [params]);

  const handleDaySelect = (date: Date) => {
    onChange?.({ t: 'd', y: date.getFullYear(), m: date.getMonth() + 1, d: date.getDate() });
    handlePopoverClose();
  };

  const handleWeekSelect = (date: Date) => {
    const y = getISOWeekYear(date);
    const w = getISOWeek(date);
    onChange?.({ t: 'w', y, w });
    handlePopoverClose();
  };

  const handleMonthSelect = (year: number, month: number) => {
    onChange?.({ t: 'm', y: year, m: month });
    handlePopoverClose();
  };

  const handleQuarterSelect = (year: number, quarter: number) => {
    onChange?.({ t: 'q', y: year, q: quarter });
    handlePopoverClose();
  };

  const handleYearSelect = (year: number) => {
    onChange?.({ t: 'y', y: year });
    handlePopoverClose();
  };

  const popoverContent = useMemo(() => {
    switch (params.t) {
      case 'd':
        return <MiniCalendar selectedDate={selectedDate} earliestDate={earliestDate} onSelect={handleDaySelect} />;
      case 'w':
        return (
          <MiniCalendar
            selectedDate={selectedDate}
            earliestDate={earliestDate}
            highlightWeek
            onSelect={handleWeekSelect}
          />
        );
      case 'm':
        return (
          <MonthSelector
            selectedYear={params.y}
            selectedMonth={params.m ?? 1}
            earliestDate={earliestDate}
            onSelect={handleMonthSelect}
          />
        );
      case 'q':
        return (
          <QuarterSelector
            selectedYear={params.y}
            selectedQuarter={params.q ?? 1}
            earliestDate={earliestDate}
            onSelect={handleQuarterSelect}
          />
        );
      case 'y':
        return <YearSelector selectedYear={params.y} earliestDate={earliestDate} onSelect={handleYearSelect} />;
    }
  }, [params, selectedDate, earliestDate]);

  return (
    <Box className={classes.stepperRoot}>
      {/* Period type tabs */}
      <Box className={classes.granularityTabs}>
        {availablePeriods.map(t => {
          const disabled = disabledPeriods.includes(t);
          const button = (
            <button
              className={clsx(classes.granularityTab, {
                [classes.granularityTabActive]: params.t === t,
                [classes.granularityTabDisabled]: disabled,
              })}
              onClick={() => handleGranularityChange(t)}
              disabled={disabled}
            >
              {PERIOD_LABELS[t]}
            </button>
          );

          return disabled && disabledPeriodTooltip ? (
            <MuiTooltip key={t} title={disabledPeriodTooltip} arrow>
              <span className={classes.granularityTabTooltipWrapper}>{button}</span>
            </MuiTooltip>
          ) : (
            <React.Fragment key={t}>{button}</React.Fragment>
          );
        })}
      </Box>

      {/* Stepper row */}
      <Box className={classes.stepperRow}>
        <IconButton
          className={clsx(classes.stepperArrow, {
            [classes.stepperArrowDisabled]: isAtEarliest,
          })}
          onClick={handleStepBackward}
          disabled={isAtEarliest}
          size="small"
        >
          <ChevronLeft />
        </IconButton>

        <Typography className={clsx(classes.stepperLabel, classes.stepperLabelClickable)} onClick={handleLabelClick}>
          {label}
        </Typography>

        <IconButton
          className={clsx(classes.stepperArrow, {
            [classes.stepperArrowDisabled]: isAtPresent,
          })}
          onClick={handleStepForward}
          disabled={isAtPresent}
          size="small"
        >
          <ChevronRight />
        </IconButton>
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ className: classes.pickerPopover }}
      >
        {popoverContent}
      </Popover>
    </Box>
  );
};

interface ExecutiveAnalyticsProps {
  filter: any;
  setFilter: any;
  thresholds?: any;
  history?: History;
  location?: Location;
}

const getParamPeriodParams = (location: Location | undefined, now: Date): PeriodParams => {
  if (!location) return currentPeriodParams('m', now);
  return parsePeriodParams(location.search, now);
};

const getParamKpi = (location?: Location): string | null => {
  if (!location) return null;
  return new URLSearchParams(location.search).get('kpi');
};

const ExecutiveAnalytics: FC<ExecutiveAnalyticsProps> = ({ filter, thresholds, history, location }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { canBypassPermissions } = useDebugMode();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const now = useMemo(() => new Date(), []);
  const periodParams = getParamPeriodParams(location, now);
  const urlKpi = getParamKpi(location);

  const [selectedMetricKey, setSelectedMetricKey] = useState<string>(urlKpi || 'fcots');
  const [metricKeysOrder, setMetricKeysOrder] = useState<string[]>(loadSavedMetricOrder);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [sendReportDialogOpen, setSendReportDialogOpen] = useState(false);
  const [userThresholds, setUserThresholds] = useState<Record<string, number>>(loadSavedThresholds);
  const selectedPeriod = toAnalyticsPeriod(periodParams.t);
  const startDate = isCurrentOrFuture(periodParams, now) ? undefined : periodParamsToStartDate(periodParams);
  const { dateRange, operationRooms, physicians, ...rest } = filter || {};

  // Fetch real data from the API.
  const {
    data: queryData,
    loading,
    error,
  } = useQuery<ExecutivePerformanceSummaryData, ExecutivePerformanceSummaryVars>(EXECUTIVE_PERFORMANCE_SUMMARY_QUERY, {
    variables: { period: selectedPeriod, startDate },
    fetchPolicy: 'cache-and-network',
  });
  const { data: earliestDateData, loading: earliestDateLoading } = useQuery<ExecutiveKpiEarliestDateData>(
    EXECUTIVE_KPI_EARLIEST_DATE_QUERY
  );
  const earliestDate = earliestDateData?.executiveKpiEarliestDate ?? undefined;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const mergedThresholds = useMemo(() => {
    const merged: Record<string, number> = {};
    for (const key of Object.keys(DEFAULT_THRESHOLDS)) {
      merged[key] = userThresholds[key] ?? DEFAULT_THRESHOLDS[key].good;
    }
    return merged;
  }, [userThresholds]);

  const handleKpiClick = useCallback(
    (key: string) => {
      setSelectedMetricKey(key);
      // Day view has no trend chart to drill into — keep the inline card view.
      if (isMobile && periodParams.t !== 'd') setDrawerOpen(true);
      if (history && location) {
        const params = new URLSearchParams(location.search);
        params.set('kpi', key);
        history.replace(`${location.pathname}?${params.toString()}`);
      }
    },
    [isMobile, history, location, periodParams.t]
  );

  // On mount (or when history/location become available): write defaults for missing params
  useEffect(() => {
    if (!history || !location) return;
    const params = new URLSearchParams(location.search);
    let changed = false;
    // Migrate legacy ?period= param to new format
    const legacyPeriod = params.get('period');
    if (legacyPeriod && !params.get('t')) {
      const legacyMap: Record<string, TimePeriod> = {
        today: 'd',
        thisWeek: 'w',
        thisMonth: 'm',
        thisQuarter: 'q',
        thisYear: 'y',
      };
      const t = legacyMap[legacyPeriod] ?? 'm';
      const defaults = currentPeriodParams(t, now);
      const newSearch = periodParamsToSearch(defaults, location.search);
      history.replace(`${location.pathname}?${newSearch}`);
      return;
    }
    if (!params.get('t')) {
      const defaults = currentPeriodParams('m', now);
      ['t', 'y', 'm', 'q', 'w', 'd'].forEach(k => params.delete(k));
      params.set('t', defaults.t);
      params.set('y', String(defaults.y));
      if (defaults.m !== undefined) params.set('m', String(defaults.m));
      changed = true;
    }
    if (!params.get('kpi')) {
      params.set('kpi', selectedMetricKey);
      changed = true;
    }
    if (changed) {
      history.replace(`${location.pathname}?${params.toString()}`);
    }
  }, [history, location?.pathname]);

  // Sync selectedMetricKey when URL kpi param changes externally
  useEffect(() => {
    const kpi = getParamKpi(location);
    if (kpi && kpi !== selectedMetricKey) {
      setSelectedMetricKey(kpi);
    }
  }, [location?.search]);

  useEffect(() => {
    if (!history || !location || earliestDateLoading || earliestDate) return;
    if (periodParams.t === 'd') return;

    const dayParams = currentPeriodParams('d', now);
    const newSearch = periodParamsToSearch(dayParams, location.search);
    history.replace(`${location.pathname}?${newSearch}`);
  }, [earliestDate, earliestDateLoading, history, location, now, periodParams.t]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setMetricKeysOrder(items => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        saveMetricOrder(newOrder); // Save to local storage
        return newOrder;
      });
    }
  }, []);

  const handleThresholdChange = useCallback((metricKey: string, value: number) => {
    setUserThresholds(prev => {
      const next = { ...prev, [metricKey]: value };
      saveThresholds(next);
      return next;
    });
  }, []);

  const sanitizedOperationRoomsFilter =
    Array.isArray(operationRooms) && operationRooms.length > 0
      ? { operationRooms: operationRooms.map(or => parseInt(or, 10)) }
      : isFinite(parseInt(operationRooms, 10))
        ? { operationRooms: [parseInt(operationRooms, 10)] }
        : {};

  const sanitizedPhysiciansFilter =
    Array.isArray(physicians) && physicians.length > 0
      ? { physicians: physicians.map(py => parseInt(py, 10)) }
      : isFinite(parseInt(physicians, 10))
        ? { physicians: [parseInt(physicians, 10)] }
        : {};

  // Map API response to current and previous data format
  // Executive KPI set: FCOTS, LPOD, turnover, utilization, cases, RTOR
  const apiData = queryData?.executivePerformanceSummary;
  const hasPreviousData = apiData?.previous != null;

  const data = useMemo(
    () => ({
      fcotsAverageDelayMinutes: apiData?.fcots?.averageDelayMinutes ?? 0,
      fcotsPercentage: apiData?.fcots?.percentage ?? 0,
      lpodAverageDelayMinutes: apiData?.lpod?.averageDelayMinutes ?? 0,
      lpodPercentage: apiData?.lpod?.percentage ?? 0,
      turnover: apiData?.turnover ?? 0,
      orsUtilization: apiData?.utilization ?? 0,
      orsCapacityMinutes: apiData?.capacity ?? null,
      numberOfCases: apiData?.cases ?? 0,
      rtor: apiData?.rtor ?? 0,
      orThroughput: 0,
      ptThroughput: 0,
      waitTime: 0,
      prepTime: 0,
      cancellation: 0,
    }),
    [apiData]
  );

  const previousData = useMemo(
    () => ({
      fcotsAverageDelayMinutes: apiData?.previous?.fcots?.averageDelayMinutes ?? data.fcotsAverageDelayMinutes,
      fcotsPercentage: apiData?.previous?.fcots?.percentage ?? data.fcotsPercentage,
      lpodAverageDelayMinutes: apiData?.previous?.lpod?.averageDelayMinutes ?? data.lpodAverageDelayMinutes,
      lpodPercentage: apiData?.previous?.lpod?.percentage ?? data.lpodPercentage,
      turnover: apiData?.previous?.turnover ?? data.turnover,
      orsUtilization: apiData?.previous?.utilization ?? data.orsUtilization,
      orsCapacityMinutes: apiData?.previous?.capacity ?? data.orsCapacityMinutes,
      numberOfCases: apiData?.previous?.cases ?? data.numberOfCases,
      rtor: apiData?.previous?.rtor ?? data.rtor,
      orThroughput: 0,
      ptThroughput: 0,
      waitTime: 0,
      prepTime: 0,
      cancellation: 0,
    }),
    [apiData, data]
  );

  const metrics: MetricDef[] = useMemo(
    () => [
      {
        key: 'fcots',
        title: 'FCOTS',
        shortTitle: 'FCOTS',
        value: data.fcotsPercentage,
        previousValue: previousData.fcotsPercentage,
        suffix: '%',
        indication: getIndicationFromThreshold(data.fcotsPercentage, 'fcots', mergedThresholds.fcots),
        description: `First Case On Time Start: Percentage of first OR cases starting on time and average delay of late starts.`,
        cardDescription: 'Percentage of first OR cases starting on time',
        inverseTrend: false,
        threshold: mergedThresholds.fcots,
        unavailable: apiData?.fcots?.percentage == null,
        previousAvailable: hasPreviousData,
        secondaryValue:
          data.fcotsAverageDelayMinutes != null ? `${Math.round(data.fcotsAverageDelayMinutes)}` : undefined,
        secondarySuffix: ' MIN',
        secondaryDescription: 'Average delay minutes across late first cases',
        tone: 'green',
        secondaryTone: 'red',
      },
      {
        key: 'lpod',
        title: 'LPOD',
        shortTitle: 'LPOD',
        value: data.lpodAverageDelayMinutes,
        previousValue: previousData.lpodAverageDelayMinutes,
        suffix: ' MIN',
        indication: getIndicationFromThreshold(data.lpodAverageDelayMinutes, 'lpod', mergedThresholds.lpod),
        description:
          'Last Patient Out Delay: Difference in minutes between the actual and scheduled Wheels-Out time of the final patient of the day.',
        cardDescription:
          selectedPeriod === 'DAY'
            ? 'Last patient out delay vs scheduled Wheels-Out'
            : 'Average last patient out delay across days in this period',
        inverseTrend: true,
        threshold: mergedThresholds.lpod,
        unavailable: apiData?.lpod?.averageDelayMinutes == null,
        previousAvailable: hasPreviousData,
        secondaryValue: selectedPeriod === 'DAY' ? undefined : formatMetricValue(data.lpodPercentage, '%'),
        secondaryDescription: selectedPeriod === 'DAY' ? undefined : 'Percentage of days the site finished late',
        tone: selectedPeriod === 'DAY' && data.lpodAverageDelayMinutes <= 0 ? 'green' : 'red',
      },
      {
        key: 'turnover',
        title: 'OR Turnover',
        shortTitle: 'OR Turnover',
        value: data.turnover,
        previousValue: previousData.turnover,
        suffix: ' MIN',
        indication: getIndicationFromThreshold(data.turnover, 'turnover', mergedThresholds.turnover),
        description: `Average OR idle time between cases, measured from previous case Wheels-Out to next case Wheels-In.`,
        cardDescription: 'Average OR idle time between consecutive cases in the same OR',
        inverseTrend: true,
        threshold: mergedThresholds.turnover,
        unavailable: apiData?.turnover == null,
        previousAvailable: hasPreviousData,
        tone: 'white',
      },
      {
        key: 'orsUtilization',
        title: 'OR Utilization',
        shortTitle: 'OR Utilization',
        value: data.orsUtilization,
        previousValue: previousData.orsUtilization,
        suffix: ' %',
        indication: getIndicationFromThreshold(data.orsUtilization, 'orsUtilization', mergedThresholds.orsUtilization),
        description: `Percentage of daily OR capacity actively used for surgery, calculated as total Wheels-In to Wheels-Out time divided by total daily OR capacity.`,
        cardDescription: 'Total Wheels-In to Wheels-Out time divided by total daily OR capacity',
        threshold: mergedThresholds.orsUtilization,
        unavailable: apiData?.utilization == null,
        previousAvailable: hasPreviousData,
        note: (() => {
          if (data.orsCapacityMinutes == null) return undefined;
          if (selectedPeriod === 'DAY') {
            return `Total OR Capacity: ${Math.round(data.orsCapacityMinutes).toLocaleString()} min`;
          }
          const capacityDays = (apiData?.trend ?? []).filter(point => (point.capacity ?? 0) > 0).length;
          const avgDailyCapacity = capacityDays > 0 ? data.orsCapacityMinutes / capacityDays : data.orsCapacityMinutes;
          return `Average daily OR capacity: ${Math.round(avgDailyCapacity).toLocaleString()} min`;
        })(),
        tone: 'white',
      },
      {
        key: 'numberOfCases',
        title: 'Number of Cases',
        shortTitle: 'Number of Cases',
        value: data.numberOfCases,
        previousValue: previousData.numberOfCases,
        suffix: '',
        indication: getIndicationFromThreshold(data.numberOfCases, 'numberOfCases', mergedThresholds.numberOfCases),
        description: `Total performed surgical cases, excluding no-shows.`,
        cardDescription: 'Total performed surgical cases, excluding no-shows',
        threshold: mergedThresholds.numberOfCases,
        previousAvailable: hasPreviousData,
        tone: 'white',
      },
      {
        key: 'rtor',
        title: 'RTOR',
        shortTitle: 'RTOR',
        value: data.rtor,
        previousValue: previousData.rtor,
        suffix: ' MIN',
        indication: getIndicationFromThreshold(data.rtor, 'rtor', mergedThresholds.rtor),
        description: `Registration to OR Time: Average time from patient registration to OR entry.`,
        cardDescription: 'Average time from patient registration to OR entry',
        inverseTrend: true,
        threshold: mergedThresholds.rtor,
        unavailable: apiData?.rtor == null,
        previousAvailable: hasPreviousData,
        tone: 'white',
      },
    ],
    [apiData, data, hasPreviousData, mergedThresholds, previousData]
  );

  const trendDataMap = useMemo(() => {
    const map: Record<string, TrendDataPoint[]> = {};
    const apiTrend = apiData?.trend ?? [];
    const operativeTrend = apiTrend.filter(point => point.cases > 0);

    if (operativeTrend.length > 0) {
      if (apiData?.fcots?.percentage != null)
        map.fcots = operativeTrend.map(point => ({
          day: point.date,
          value: point.fcots?.percentage ?? 0,
          secondary: point.fcots?.averageDelayMinutes ?? undefined,
        }));
      if (apiData?.lpod?.averageDelayMinutes != null)
        map.lpod = operativeTrend.map(point => ({ day: point.date, value: point.lpod?.averageDelayMinutes ?? 0 }));
      if (apiData?.turnover != null)
        map.turnover = operativeTrend.map(point => ({ day: point.date, value: point.turnover ?? 0 }));
      if (apiData?.utilization != null)
        map.orsUtilization = operativeTrend.map(point => ({ day: point.date, value: point.utilization ?? 0 }));
      map.numberOfCases = operativeTrend.map(point => ({ day: point.date, value: point.cases }));
      if (apiData?.rtor != null) map.rtor = operativeTrend.map(point => ({ day: point.date, value: point.rtor ?? 0 }));
    }

    return map;
  }, [apiData]);

  const orderedMetrics = useMemo(
    () =>
      [...metrics].sort((a, b) => {
        const indexA = metricKeysOrder.indexOf(a.key);
        const indexB = metricKeysOrder.indexOf(b.key);
        return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
      }),
    [metrics, metricKeysOrder]
  );

  const selectedMetric = metrics.find(m => m.key === selectedMetricKey) || metrics[0];
  const selectedTrendData = trendDataMap[selectedMetric.key] ?? [];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.glowTop} />
      <Box className={classes.glowBottom} />
      <Box className={classes.adminButtonContainer}>
        {canBypassPermissions && (
          <IconButton size="small" onClick={() => setAdminDialogOpen(true)} className={classes.adminButton}>
            <Tune style={{ fontSize: 18 }} />
          </IconButton>
        )}
        {canBypassPermissions && (periodParams.t === 'w' || periodParams.t === 'm') && (
          <IconButton size="small" onClick={() => setSendReportDialogOpen(true)} className={classes.adminButton}>
            <Send style={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>
      {canBypassPermissions && (
        <ExecutiveAdminDialog open={adminDialogOpen} onClose={() => setAdminDialogOpen(false)} />
      )}
      {canBypassPermissions && (periodParams.t === 'w' || periodParams.t === 'm') && (
        <SendExecutiveReportDialog
          open={sendReportDialogOpen}
          onClose={() => setSendReportDialogOpen(false)}
          periodParams={periodParams}
          periodLabel={getStepperLabel(periodParams, now)}
        />
      )}
      {error && !apiData && (
        <Box mb={2}>
          <Alert severity="warning">Live executive data is unavailable.</Alert>
        </Box>
      )}

      {periodParams.t === 'd' && !isMobile ? (
        // Day view (desktop): full-width grid of styled KPI tiles
        <Box className={classes.dayTileGrid}>
          {orderedMetrics.map(metric => (
            <DayKpiTile
              key={metric.key}
              data={{
                key: metric.key,
                label: metric.title,
                value: metric.value,
                suffix: metric.suffix,
                secondaryValue: metric.secondaryValue,
                secondarySuffix: metric.secondarySuffix,
                secondaryDescription: metric.secondaryDescription,
                previousValue: metric.previousValue,
                previousAvailable: metric.previousAvailable,
                inverseTrend: metric.inverseTrend,
                unavailable: metric.unavailable,
                tone: metric.tone,
                secondaryTone: metric.secondaryTone,
                note: metric.note,
              }}
            />
          ))}
        </Box>
      ) : (
        <Box className={classes.deepDiveLayout}>
          {/* Left Column - KPI Cards */}
          <Box className={classes.kpiSidebar}>
            {!isMobile ? (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={orderedMetrics.map(m => m.key)} strategy={verticalListSortingStrategy}>
                  {orderedMetrics.map((metric, index) => (
                    <SortableKpiCard
                      key={metric.key}
                      id={metric.key}
                      index={index}
                      metric={metric}
                      isSelected={selectedMetricKey === metric.key}
                      onClick={() => handleKpiClick(metric.key)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            ) : (
              orderedMetrics.map((metric, index) => (
                <KpiCard
                  key={metric.key}
                  index={index}
                  metric={metric}
                  isSelected={selectedMetricKey === metric.key}
                  onClick={() => handleKpiClick(metric.key)}
                />
              ))
            )}
          </Box>

          {/* Right Column - Trend Intelligence (desktop only) */}
          {!isMobile && (
            <Box className={classes.trendMain}>
              <TrendPanel
                metric={selectedMetric}
                trendData={selectedTrendData}
                periodKey={periodParams.t}
                onThresholdChange={handleThresholdChange}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Mobile drawer for chart */}
      {isMobile && (
        <SwipeableDrawer
          anchor="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onOpen={() => setDrawerOpen(true)}
          disableSwipeToOpen
          PaperProps={{ className: classes.drawerPaper }}
        >
          <Box className={classes.drawerHandle} />
          <Box className={classes.drawerHeader}>
            <Typography className={classes.drawerTitle}>{selectedMetric.title}</Typography>
            <IconButton className={classes.drawerCloseButton} onClick={() => setDrawerOpen(false)} size="small">
              <Close style={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <TrendPanel
            metric={selectedMetric}
            trendData={selectedTrendData}
            periodKey={periodParams.t}
            onThresholdChange={handleThresholdChange}
          />
        </SwipeableDrawer>
      )}
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    position: 'relative',
    minHeight: '70vh',
    [theme.breakpoints.down('sm')]: {
      // padding: theme.spacing(2),
    },
  },
  adminButtonContainer: {
    position: 'absolute',
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  adminButton: {
    color: 'rgba(255, 255, 255, 0.35)',
    '&:hover': {
      color: 'rgba(34, 211, 238, 0.8)',
      background: 'rgba(34, 211, 238, 0.08)',
    },
  },
  glowTop: {
    position: 'absolute',
    top: -100,
    left: '20%',
    width: 500,
    height: 500,
    background: 'radial-gradient(circle, rgba(59, 130, 246, 0.12) 0%, transparent 70%)',
    pointerEvents: 'none',
    filter: 'blur(60px)',
    zIndex: 0,
    [theme.breakpoints.down('sm')]: {
      width: 300,
      height: 300,
    },
  },
  glowBottom: {
    position: 'absolute',
    bottom: -100,
    right: '20%',
    width: 400,
    height: 400,
    background: 'radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, transparent 70%)',
    pointerEvents: 'none',
    filter: 'blur(60px)',
    zIndex: 0,
    [theme.breakpoints.down('sm')]: {
      width: 250,
      height: 250,
    },
  },
  sectionLabel: {
    fontSize: '0.65rem',
    letterSpacing: '0.25em',
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.35)',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(2),
    position: 'relative',
    zIndex: 1,
  },

  // Two-column deep dive layout
  deepDiveLayout: {
    position: 'relative',
    zIndex: 1,
    display: 'grid',
    flex: 1,
    gridTemplateColumns: '340px 1fr',
    gap: theme.spacing(2.5),
    minHeight: 520,
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '280px 1fr',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      minHeight: 'auto',
    },
  },
  // Day view — KPI tiles form a responsive grid that fills the full container
  dayTileGrid: {
    position: 'relative',
    zIndex: 1,
    flex: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gridAutoRows: '1fr',
    gap: theme.spacing(2),
    minHeight: 520,
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
      minHeight: 'auto',
    },
  },

  // KPI Sidebar
  kpiSidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    overflowY: 'auto',
    // maxHeight: 600,
    paddingRight: theme.spacing(0.5),
    '&::-webkit-scrollbar': {
      width: 4,
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(255,255,255,0.02)',
      borderRadius: 2,
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255,255,255,0.08)',
      borderRadius: 2,
    },
    '& > *': {
      flex: 1,
      display: 'flex',
    },
    // [theme.breakpoints.down('sm')]: {
    //   flexDirection: 'row',
    //   overflowX: 'auto',
    //   overflowY: 'hidden',
    //   maxHeight: 'none',
    //   paddingRight: 0,
    //   paddingBottom: theme.spacing(0.5),
    //   paddingLeft: theme.spacing(2),
    //   scrollPaddingRight: theme.spacing(2),
    //   marginLeft: theme.spacing(-2),
    //   marginRight: theme.spacing(-2),
    //   gap: theme.spacing(1),
    //   scrollbarWidth: 'none' as const,
    // },
  },

  // KPI Card
  kpiCard: {
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    borderRadius: 2,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2, 2.5),
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.05)',
      borderColor: 'rgba(255, 255, 255, 0.12)',
      transform: 'translateX(4px)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 1,
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)',
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: 160,
      flexShrink: 0,
      '&:hover': {
        transform: 'translateY(-2px)',
      },
    },
  },
  kpiCardSelected: {
    background: 'rgba(34, 211, 238, 0.06)',
    borderColor: 'rgba(34, 211, 238, 0.4)',
    boxShadow: '0 0 20px rgba(34, 211, 238, 0.1), inset 0 0 20px rgba(34, 211, 238, 0.03)',
    '&:hover': {
      background: 'rgba(34, 211, 238, 0.08)',
      borderColor: 'rgba(34, 211, 238, 0.5)',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      width: 3,
      background: 'linear-gradient(180deg, #22d3ee, rgba(34, 211, 238, 0.3))',
      borderRadius: '3px 0 0 3px',
    },
  },
  kpiCardHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
  },
  kpiCardIndex: {
    fontSize: '0.7rem',
    letterSpacing: '0.12em',
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.45)',
    textTransform: 'uppercase',
  },
  kpiCardContent: {
    display: 'flex',
    flex: 1,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  kpiCardBody: {
    display: 'flex',
  },
  kpiCardValue: {
    fontSize: '2.5rem',
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
    display: 'flex',
    alignItems: 'center',
  },
  kpiCardUnit: {
    fontSize: '0.85rem',
    fontWeight: 500,
    marginLeft: 3,
    opacity: 0.6,
  },
  kpiCardTrend: {
    display: 'flex',
    alignItems: 'center',
  },
  kpiCardTrendText: {
    fontSize: '1.15rem',
    fontWeight: 700,
    letterSpacing: '0.02em',
  },

  // Trend Main Panel
  trendMain: {
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: 0,
  },
  trendPanel: {
    background: 'rgba(255, 255, 255, 0.02)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    borderRadius: 2,
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    position: 'relative' as const,
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 1,
      background: 'linear-gradient(90deg, transparent, rgba(34, 211, 238, 0.15), transparent)',
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      height: 'auto',
      border: 'none',
      background: 'transparent',
      backdropFilter: 'none',
      overflow: 'visible',
      '&::before': {
        display: 'none',
      },
    },
  },
  trendHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column' as const,
      gap: theme.spacing(1.5),
    },
  },
  trendBadge: {
    display: 'inline-flex',
    border: '1px solid rgba(34, 211, 238, 0.3)',
    borderRadius: 20,
    padding: '4px 14px',
    marginBottom: theme.spacing(1),
    background: 'rgba(34, 211, 238, 0.06)',
  },
  trendBadgeText: {
    fontSize: '0.6rem',
    fontWeight: 700,
    letterSpacing: '0.15em',
    color: '#22d3ee',
    textTransform: 'uppercase' as const,
  },
  trendTitle: {
    fontSize: '2rem',
    fontWeight: 800,
    color: '#fff',
    letterSpacing: '-0.02em',
    lineHeight: 1.1,
    marginBottom: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
    },
  },
  trendDescription: {
    fontSize: '0.8rem',
    color: 'rgba(255, 255, 255, 0.4)',
    lineHeight: 1.5,
    maxWidth: 420,
  },
  trendNote: {
    fontSize: '0.72rem',
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.3)',
    letterSpacing: '0.01em',
    marginTop: 4,
  },
  trendLiveValue: {
    textAlign: 'right' as const,
    [theme.breakpoints.down('sm')]: {
      textAlign: 'left' as const,
    },
  },
  trendLiveLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.18em',
    color: 'rgba(255, 255, 255, 0.5)',
    textTransform: 'uppercase' as const,
    marginTop: 6,
  },
  trendLiveNumber: {
    fontSize: '3rem',
    fontWeight: 800,
    lineHeight: 1,
    letterSpacing: '-0.03em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  },
  trendLiveUnit: {
    fontSize: '1.2rem',
    fontWeight: 500,
    marginLeft: 3,
    opacity: 0.6,
  },

  // Chart container
  trendChartContainer: {
    flex: 1,
    minHeight: 280,
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flex: 'none',
      height: 'auto',
      minHeight: 260,
    },
  },


  // Footer
  trendFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(4),
    paddingTop: theme.spacing(2),
    borderTop: '1px solid rgba(255, 255, 255, 0.06)',
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(2),
    },
  },
  trendFooterItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
  },
  trendFooterLabel: {
    fontSize: '0.85rem',
    fontWeight: 700,
    letterSpacing: '0.14em',
    color: 'rgba(255, 255, 255, 0.45)',
    textTransform: 'uppercase' as const,
  },
  trendFooterValue: {
    fontSize: '1.15rem',
    fontWeight: 700,
    letterSpacing: '0.02em',
  },

  // Mobile drawer
  drawerPaper: {
    background: '#081546',
    backdropFilter: 'blur(20px)',
    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '16px 16px 0 0',
    maxHeight: '92vh',
    padding: theme.spacing(0, 2, 3, 2),
    overflow: 'auto',
  },
  drawerHandle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    background: 'rgba(255, 255, 255, 0.15)',
    margin: '10px auto 8px',
  },
  drawerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  drawerTitle: {
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.15em',
    color: 'rgba(255, 255, 255, 0.35)',
    textTransform: 'uppercase' as const,
  },
  drawerCloseButton: {
    color: 'rgba(255, 255, 255, 0.35)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: 2,
    padding: 4,
    '&:hover': {
      color: 'rgba(255, 255, 255, 0.6)',
      background: 'rgba(255, 255, 255, 0.04)',
    },
  },

  // Threshold settings
  thresholdButton: {
    color: 'rgba(255, 255, 255, 0.25)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    borderRadius: 2,
    padding: 6,
    marginTop: 2,
    transition: 'all 0.25s ease',
    '&:hover': {
      color: 'rgba(255, 255, 255, 0.6)',
      borderColor: 'rgba(255, 255, 255, 0.15)',
      background: 'rgba(255, 255, 255, 0.04)',
    },
  },
  thresholdPopover: {
    background: 'rgba(15, 23, 42, 0.97)',
    backdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: 2,
    padding: theme.spacing(2),
    minWidth: 200,
  },
  thresholdPopoverLabel: {
    fontSize: '0.6rem',
    fontWeight: 700,
    letterSpacing: '0.15em',
    color: 'rgba(255, 255, 255, 0.35)',
    textTransform: 'uppercase' as const,
    marginBottom: theme.spacing(1),
  },
  thresholdInputRow: {
    display: 'flex',
    alignItems: 'center',
    background: 'rgba(255, 255, 255, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: 2,
    padding: '4px 10px',
    marginBottom: theme.spacing(1.5),
    transition: 'border-color 0.2s ease',
    '&:focus-within': {
      borderColor: 'rgba(34, 211, 238, 0.4)',
    },
  },
  thresholdInput: {
    flex: 1,
    color: '#fff',
    fontSize: '0.95rem',
    fontWeight: 600,
    '& input': {
      padding: '6px 0',
      '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
      MozAppearance: 'textfield',
    },
  },
  thresholdInputSuffix: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.3)',
    marginLeft: theme.spacing(0.5),
  },
  thresholdApplyButton: {
    display: 'flex',
    justifyContent: 'center',
    padding: '6px 0',
    borderRadius: 2,
    background: 'rgba(34, 211, 238, 0.1)',
    border: '1px solid rgba(34, 211, 238, 0.25)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'rgba(34, 211, 238, 0.18)',
      borderColor: 'rgba(34, 211, 238, 0.4)',
    },
  },
  thresholdApplyText: {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    color: '#22d3ee',
    textTransform: 'uppercase' as const,
  },

  // Granularity + Stepper
  stepperRoot: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1),
      flexDirection: 'column' as const,
    },
  },
  granularityTabs: {
    display: 'flex',
    gap: 2,
    background: 'rgba(255, 255, 255, 0.04)',
    borderRadius: 8,
    padding: 3,
    border: '1px solid rgba(255, 255, 255, 0.06)',
  },
  granularityTab: {
    padding: '6px 18px',
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: 'rgba(255, 255, 255, 0.45)',
    background: 'transparent',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
    '&:hover': {
      color: 'rgba(255, 255, 255, 0.7)',
      background: 'rgba(255, 255, 255, 0.04)',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '5px 10px',
      fontSize: '0.68rem',
    },
  },
  granularityTabActive: {
    color: '#fff',
    background: 'rgba(59, 130, 246, 0.25)',
    boxShadow: '0 0 12px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255,255,255,0.06)',
    '&:hover': {
      color: '#fff',
      background: 'rgba(59, 130, 246, 0.3)',
    },
  },
  granularityTabDisabled: {
    color: 'rgba(255, 255, 255, 0.22)',
    cursor: 'not-allowed',
    '&:hover': {
      color: 'rgba(255, 255, 255, 0.22)',
      background: 'transparent',
    },
  },
  granularityTabTooltipWrapper: {
    display: 'flex',
    alignItems: 'stretch',
  },
  stepperRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(0.5),
    },
  },
  stepperArrow: {
    color: 'rgba(255, 255, 255, 0.6)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: 6,
    width: 32,
    height: 32,
    transition: 'all 0.2s ease',
    '&:hover': {
      color: '#fff',
      background: 'rgba(255, 255, 255, 0.08)',
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
  },
  stepperArrowDisabled: {
    opacity: 0.25,
    cursor: 'default',
    '&:hover': {
      color: 'rgba(255, 255, 255, 0.6)',
      background: 'transparent',
      borderColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  stepperLabel: {
    fontSize: '0.9rem',
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.85)',
    letterSpacing: '0.02em',
    minWidth: 220,
    textAlign: 'center' as const,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
      minWidth: 160,
    },
  },
  stepperLabelClickable: {
    cursor: 'pointer',
    borderRadius: 6,
    padding: '4px 12px',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.06)',
    },
  },

  // Period picker popover
  pickerPopover: {
    background: '#151a27',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: 12,
    padding: theme.spacing(1),
    minWidth: 280,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
  },

  // Mini calendar (day/week picker)
  miniCal: {
    padding: theme.spacing(1),
    userSelect: 'none' as const,
  },
  miniCalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    '& .MuiIconButton-root': {
      color: 'rgba(255, 255, 255, 0.6)',
      padding: 4,
      '&:hover': { color: '#fff' },
      '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.15)' },
    },
  },
  miniCalTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  miniCalDayNames: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 2,
    marginBottom: 4,
  },
  miniCalDayName: {
    fontSize: '0.65rem',
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.35)',
    textAlign: 'center' as const,
    textTransform: 'uppercase' as const,
    padding: '2px 0',
  },
  miniCalGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 2,
  },
  miniCalCell: {
    // Reset button defaults
    appearance: 'none' as const,
    background: 'none',
    border: 'none',
    outline: 'none',
    fontFamily: 'inherit',
    // Layout
    aspectRatio: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    width: 36,
    height: 36,
    padding: 0,
  },
  miniCalDay: {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.7)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:hover': {
      background: 'rgba(59, 130, 246, 0.2)',
      color: '#fff',
    },
  },
  miniCalDaySelected: {
    background: 'rgba(59, 130, 246, 0.4) !important',
    color: '#fff !important',
    fontWeight: 700,
    boxShadow: '0 0 8px rgba(59, 130, 246, 0.25)',
  },
  miniCalDayToday: {
    border: '1px solid rgba(59, 130, 246, 0.5)',
    color: '#93c5fd',
  },
  miniCalDayDisabled: {
    color: 'rgba(255, 255, 255, 0.12) !important',
    cursor: 'default',
    pointerEvents: 'none' as const,
    '&:hover': {
      background: 'none',
    },
  },

  // Grid selectors (month, quarter, year)
  gridSelector: {
    padding: theme.spacing(1),
    userSelect: 'none' as const,
  },
  monthGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 6,
  },
  quarterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 8,
  },
  yearGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 6,
  },
  gridCell: {
    // Reset button defaults
    appearance: 'none' as const,
    background: 'none',
    border: 'none',
    outline: 'none',
    fontFamily: 'inherit',
    // Layout
    padding: '10px 8px',
    borderRadius: 8,
    textAlign: 'center' as const,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.8rem',
    fontWeight: 500,
    '&:hover': {
      background: 'rgba(59, 130, 246, 0.15)',
      color: '#fff',
    },
  },
  gridCellSelected: {
    background: 'rgba(59, 130, 246, 0.4) !important',
    color: '#fff !important',
    fontWeight: 700,
    boxShadow: '0 0 8px rgba(59, 130, 246, 0.25)',
  },
  gridCellDisabled: {
    color: 'rgba(255, 255, 255, 0.12) !important',
    cursor: 'default',
    pointerEvents: 'none' as const,
    '&:hover': {
      background: 'none',
    },
  },
  quarterCell: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 2,
    padding: '12px 8px',
  },
  quarterLabel: {
    fontSize: '0.85rem',
    fontWeight: 600,
  },
  quarterRange: {
    fontSize: '0.65rem',
    opacity: 0.6,
  },
}));

export default ExecutiveAnalytics;
